<template>
  <section class="section" id="purchases-panel">
    <div class="container">
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <purchases-title
        :purchasesQty="purchasesQty"
        @get-purchases="getPurchases"
      />

      <div
        v-if="
          permissions.addUpdatePurchase || permissions.bulkAddUpdatePurchases
        "
      >
        <add-purchases
          @search-product="searchProduct"
          :processingPurchases="processingPurchases"
          @processing-purchases="processingPurchases = true"
        />

        <p class="explanation has-text-centered">
          <br />
          {{ $t("purchases.addCostExplanationNote1") }}
          <a
            :href="spreadsheetModelUrl"
            :download="spreadsheetModelFilename"
            v-if="permissions.bulkAddUpdatePurchases"
          >
            {{ $t("purchases.thisSpreadsheetModel") }}</a
          >
          <br />
          {{ $t("purchases.addCostExplanationNote2") }}
        </p>
      </div>

      <h2 v-if="showEmptyState" class="subtitle has-text-centered">
        <br />
        {{ $t("purchases.noCostInformationAtThisMoment") }}
      </h2>

      <div v-else>
        <b-pagination
          v-if="purchasesQty > 0"
          :total="purchasesQty"
          v-model="current"
          order="is-centered"
          :per-page="perPage"
          aria-next-label="Próxima página"
          aria-previous-label="Página anterior"
          aria-page-label="Página"
          aria-current-label="Página atual"
          @change="changePage"
        ></b-pagination>

        <purchases-table
          :purchases="purchases"
          :search-barcode="searchBarcode"
          :search-name="searchName"
          :sort-field="sortField"
          :sort-order="sortOrder"
          @update-search-barcode="updateSearchBarcode"
          @update-search-name="updateSearchName"
          @update-sort="updateSort"
          @update-cost="savePurchase"
        />

        <b-pagination
          v-if="purchasesQty > 0"
          :total="purchasesQty"
          v-model="current"
          order="is-centered"
          :per-page="perPage"
          aria-next-label="Próxima página"
          aria-previous-label="Página anterior"
          aria-page-label="Página"
          aria-current-label="Página atual"
          @change="changePage"
        ></b-pagination>
      </div>
    </div>

    <b-modal :active.sync="isConfirmPurchaseModalOpen">
      <confirm-purchase-modal
        :product="product"
        @add-product-to-purchases="savePurchase"
      />
    </b-modal>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import purchasesTitle from "./PurchasesTitle";
import addPurchases from "./AddPurchases";
import confirmPurchaseModal from "./ConfirmPurchaseModal";
import purchasesTable from "./PurchasesTable";

export default {
  mixins: [common],

  components: {
    purchasesTitle,
    addPurchases,
    confirmPurchaseModal,
    purchasesTable
  },

  data() {
    return {
      isLoading: false,
      isConfirmPurchaseModalOpen: false,
      product: {},
      processingPurchases: true,
      current: 1,
      perPage: 100,
      searchBarcode: "",
      searchName: "",
      sortField: "n",
      sortOrder: "asc",
      searchTimeout: null
    };
  },

  methods: {
    getPurchases(skip) {
      if (isNaN(skip)) {
        skip = (this.current - 1) * this.perPage;
      }

      if (skip < 0) skip = 0;

      this.isLoading = true;
      return this.$store
        .dispatch("getPurchases", {
          skip,
          limit: this.perPage,
          barcode: this.searchBarcode,
          name: this.searchName,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.processingPurchases = res.data.payload.processingPurchases;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    changePage(page) {
      const skip = (page - 1) * this.perPage;
      this.getPurchases(skip);
    },

    queueSearch() {
      this.current = 1;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getPurchases(0);
      }, 350);
    },

    updateSearchBarcode(value) {
      this.searchBarcode = value;
      this.queueSearch();
    },

    updateSearchName(value) {
      this.searchName = value;
      this.queueSearch();
    },

    updateSort({ field, order }) {
      this.sortField = field;
      this.sortOrder = order;
      this.current = 1;
      this.getPurchases(0);
    },

    searchProduct(barcode) {
      if (!/\d+|^CB+\d+$/.test(barcode)) {
        this.alertError({ message: "INVALID_BARCODE" });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("searchProduct", { barcode })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.isConfirmPurchaseModalOpen = true;
            this.product = res.data.payload.product;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    savePurchase(product) {
      this.isLoading = true;
      this.$store
        .dispatch("savePurchase", product)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.isConfirmPurchaseModalOpen = false;
            return this.getPurchases();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  computed: {
    ...mapGetters([
      "purchases",
      "purchasesQty",
      "permissions",
      "locale",
      "address"
    ]),

    usesEnglishSpreadsheetModel() {
      return this.locale === "en" || this.address.country === "United Arab Emirates";
    },

    spreadsheetModelFilename() {
      return this.usesEnglishSpreadsheetModel
        ? "Onii-Template_upload_costs_products.xlsx"
        : "Onii-Modelo_upload_custos_produtos.xlsx";
    },

    spreadsheetModelUrl() {
      const folder = this.usesEnglishSpreadsheetModel ? "en" : "pt";
      return `https://s3.amazonaws.com/assets.onii.com.br/models/${folder}/${this.spreadsheetModelFilename}`;
    },

    hasActiveFilters() {
      return !!this.searchBarcode || !!this.searchName;
    },

    showEmptyState() {
      return this.purchasesQty === 0 && !this.hasActiveFilters;
    }
  },

  mounted() {
    this.getPurchases();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  }
};
</script>

<style scoped>
.explanation {
  /* max-width: 800px; */
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 30px;
}
#purchases-panel {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}
</style>
